/* eslint-disable no-param-reassign */
import ImagePreparePlugin, { ImageBlobAndString, SUPPORT_IMAGE_TYPES } from '../plugin/image-prepare-plugin';
import TextPreparePlugin from '../plugin/text-prepare-plugin';
import HTMLPreparePlugin, { HTMLType } from '../plugin/html-prepare-plugin';
import ua from '@tencent/docs-user-agent';
import { ClipboardType } from '../types';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import SnackbarMobile from '@tencent/dui-mobile/lib/components/Snackbar';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import i18n from '@i18next/i18n';
import { ClipboardData, ICopyPasteHandler } from '../base/base-copy-paste.interface';
import { IClipboardDeviceDelegate, ClipboardDeviceDelegate } from './clipboard-delegate';
import {
    ICanvasRendererService,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/base/canvas-renderer-service.interface';
import { ICopyPasteInjectable } from '@src/slide/application/service/copy-paste/inject/copy-paste-injectable.interface';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';

const snackbar = browser.isMobile ? SnackbarMobile : Snackbar;

interface ClipboardItem {
    readonly types: ReadonlyArray<string>;
    getType(type: string): Promise<Blob>;
}

type ClipboardItems = ClipboardItem[];

declare let ClipboardItem: {
    prototype: ClipboardItem;
    new(
        items: Record<string, string | Blob | PromiseLike<string | Blob>>,
        options?: {
            presentationStyle?: 'attachment' | 'inline' | 'unspecified';
        }
    ): ClipboardItem;
};

interface ClipboardOption {
    override: boolean;
}

type IClipboardManagerInjectable = Pick<
    Partial<ICopyPasteInjectable>,
    'onClipboardManagerInit' | 'supportAsyncClipboardApi' | 'useMemoryClipboard' | 'currentEnvIsInPrivate'
>;

class ClipboardManager {
    public textPreparePlugin: TextPreparePlugin;
    public imagePreparePlugin: ImagePreparePlugin;
    public htmlPreparePlugin: HTMLPreparePlugin;
    private copyDataType?: HTMLType; // 复制的数据类型，上报用
    private handler: ICopyPasteHandler;
    private clipboardData: ClipboardData;
    private writeTimer: NodeJS.Timeout;
    private startWriteTime: Date | null = null;
    private startCopyTime: number;
    private writeLoading: any;
    private clipboardDeviceDelegate: IClipboardDeviceDelegate;
    private contentRendererService?: ICanvasRendererService;
    public constructor(
        copyPasteHandler: ICopyPasteHandler,
        private clipboardManagerInjectable: IClipboardManagerInjectable,
    ) {
        this.handler = copyPasteHandler;
        this.clipboardData = {};
        this.textPreparePlugin = new TextPreparePlugin();
        this.imagePreparePlugin = new ImagePreparePlugin();
        this.htmlPreparePlugin = new HTMLPreparePlugin(this.clipboardManagerInjectable as any);
        this.clipboardManagerInjectable.onClipboardManagerInit?.();
        this.clipboardDeviceDelegate = new ClipboardDeviceDelegate(this.clipboardManagerInjectable as any);
    }

    /**
     * 保存即将写入剪贴板的数据，此时是写入内存中
     * @param {ClipboardData} clipboardConfig 要写入剪贴板的内容
     * @param {ClipboardOption} clipboardOption 剪贴板配置信息
     */
    public save(clipboardConfig: ClipboardData, clipboardOption?: ClipboardOption): void {
        if (clipboardConfig?.[ClipboardType.HTML]) {
            this.copyDataType = clipboardConfig[ClipboardType.HTML]?.type;
        }
        if (clipboardOption?.override) {
            this.deleteClipboardData();
        }
        this.updateClipboardData(clipboardConfig);
    }

    /**
     * 读取剪贴板的过程
     * 不支持异步api的从event事件中读取
     * 支持异步api的从navigator中读取
     * @param event
     * @param clipboardType
     * @returns
     */
    public read(event?: ClipboardEvent): any {
        // TODO 目前异步API读取剪贴板在window chrome上数据有问题，待管饭修复
        // if (this.isSupportAsyncApi) {
        //     return this.handlerAsyncApiRead(clipboardType);
        // }
        if (ua.isMobile) {
            return this.readDataFromClientClipboard();
        }
        return this.handlerSyncApiRead(event);
    }

    public async readFile(event: ClipboardEvent): Promise<File[] | undefined> {
        // 移动端采取之前策略
        // 不读取剪切板中的文件区域
        // (ps: 小程序中粘贴会变慢)
        if (ua.isMobile) {
            return Promise.resolve([]);
        }
        return this.clipboardDeviceDelegate.read({
            params: {
                adapter: {
                    event,
                    readFile: true,
                },
            },
        });
    }

    public startCopy() {
        this.startCopyTime = Date.now();
    }

    /**
     * 写入剪贴板的过程
     * 不支持异步api的使用event写入，且整个过程中不能有异步，否则写入失败，这也是逻辑拆出来的原因
     * 支持异步api的是mobile和部分CEF/Webkit浏览器
     * @param {ClipboardType} clipboardEvent 剪贴板数据类型
     * @returns
     */
    public write(clipboardEvent?: ClipboardEvent): Promise<void> | undefined {
        this.startWriteTimer();
        if (this.isAllDataWritten()) {
            this.endWriteTimer();
            return;
        }
        if (!this.clipboardManagerInjectable.supportAsyncClipboardApi) {
            this.deleteClipboardData([ClipboardType.IMAGE_PNG]);
            this.syncWriteToClipboard(clipboardEvent);
            this.handler?.successWrite();
            this.handler?.finallyWrite(this.clipboardData);
            this.endWriteTimer();
            return;
        }
        if (this.isForceAsyncModel) return this.writeToClipboard(clipboardEvent, true).then(() => this.endWriteTimer());
        const copyClipboardData = Object.assign({}, this.clipboardData);
        // 图片转换写入开销比较大，先把 text 和 html 写入，分 2 步走
        return this.writeToClipboardPreprocessing(clipboardEvent, false)
            .then((syncClipboardItemConf?: any) => {
                const needHandleImg = !!copyClipboardData[ClipboardType.IMAGE_PNG];
                if (!needHandleImg && !syncClipboardItemConf) return;
                this.clipboardData = copyClipboardData;
                // IMAGE 格式只支持使用异步 api 写入
                window.log?.info('[copyPasteManager] 图片类型写入剪贴板');
                return this.writeToClipboard(clipboardEvent, true, syncClipboardItemConf);
            })
            .catch(() => {
                window.log?.error('[copyPasteManager] 写入剪切板失败');
            })
            .finally(() => {
                this.endWriteTimer();
            });
    }

    public setServicesDelegate(services: { contentRendererService?: ICanvasRendererService }) {
        this.contentRendererService = services.contentRendererService;
    }

    /**
     * 使用event同步读取剪贴板
     * @param {ClipboardEvent} event
     * @returns
     */
    private async handlerSyncApiRead(event?: ClipboardEvent) {
        if (!event) return {};
        // slide-legacy-clipboard  读取剪切板获取的是 html 和 text 字段
        // tencent-clipboard 读取剪切板获取的是 html 和 plain 字段
        const { html, text, plain } = (await this.clipboardDeviceDelegate.read<Record<'html'| 'text'| 'plain', string>>({
            params: {
                adapter: {
                    event,
                },
            },
        })) || {};
        return {
            [ClipboardType.HTML]: this.handlerStringToString(ClipboardType.HTML, html),
            [ClipboardType.TEXT]: this.handlerStringToString(ClipboardType.TEXT, text || plain),
        };
    }

    /**
     * 使用navigator异步写入剪贴板，mobile使用客户端接口写入
     * @param {ClipboardType} clipboardType
     * @returns
     */
    private async handlerAsyncApiRead(clipboardType?: ClipboardType) {
        let clipboardDataMap = {};
        try {
            if (ua.isMobile) {
                clipboardDataMap = await this.readDataFromClientClipboard();
            } else {
                clipboardDataMap = await this.asyncReadClipboard(clipboardType);
            }
        } catch (e) {
            window?.log.warn('[copyPasteManager] read clipboard failed, maybe is empty');
        }
        return clipboardDataMap;
    }

    private get validClipboardTypeArr() {
        return Object.values(ClipboardType);
    }

    private get isForceAsyncModel() {
        return this.handler.isForceAsyncModel();
    }

    /**
     * 目前移动端存活于各个客户端内，使用端内剪贴板读取数据
     * @returns
     */
    private async readDataFromClientClipboard() {
        const { plain, html } = await this.clipboardDeviceDelegate.read<{ plain: string; html: string }>({
            options: {
                useMemoryClipboard: this.clipboardManagerInjectable.useMemoryClipboard,
            },
        }) || {};
        window.log.info('[ClipboardManager] read clipboard data:', { plain, html });
        return {
            [ClipboardType.TEXT]: this.handlerStringToString(ClipboardType.TEXT, plain),
            [ClipboardType.HTML]: this.handlerStringToString(ClipboardType.HTML, html),
        };
    }

    private writeToClipboardPreprocessing(clipboardEvent?: ClipboardEvent | undefined, useAsyncApi?: boolean) {
        // 私有化与SaaS中分两步走会存在剪切板覆盖问题，需要支持一次写入剪切板
        // 前置处理私有化与SaaS存在图片的情况，需跳过第一次写入剪切板
        if (this.clipboardManagerInjectable.currentEnvIsInPrivate) {
            const hasImage = this.clipboardData[ClipboardType.IMAGE_PNG];
            // 移动端没有图片类型，所以不需要额外 ua 判断
            if (hasImage) {
                window.log?.info('[ClipboardManager] 跳过文本类型写入剪贴板，等待与图片类型一并写入。');
                // 执行 getSyncClipboardItemConf 之前需要 deleteClipboardData IMAGE_PNG 不然一直处在复制中
                this.deleteClipboardData([ClipboardType.IMAGE_PNG]);
                const syncClipboardItemConf = this.getSyncClipboardItemConf();
                return Promise.resolve(syncClipboardItemConf);
            }
        }

        this.deleteClipboardData([ClipboardType.IMAGE_PNG]);
        const hasOtherType = Object.keys(this.clipboardData).length;
        if (!hasOtherType) return Promise.resolve();
        window.log?.info('[ClipboardManager] 文本类型写入剪贴板');

        return this.writeToClipboard(clipboardEvent, useAsyncApi);
    }

    /**
     * 检测是否所有数据已经写入剪贴板
     * @returns {boolean} 是否有未写入数据
     */
    private isAllDataWritten() {
        return !Object.values(this.clipboardData).filter(Boolean).length;
    }

    /**
     * 检测剪贴板是否为空
     * @returns {boolean} 是否为空
     */
    // private async isEmpty() {
    //     const clipboardItems: ClipboardItems = await navigator.clipboard.read();
    //     const [clipboardItem] = clipboardItems;
    //     return !Array.from(clipboardItem.types).length;
    // }

    /**
     * 异步读取剪贴板内容
     * @param {ClipboardType} clipboardType 需要读取的内容类型
     * @returns {Promise<any>}
     */
    private async asyncReadClipboard(clipboardType?: ClipboardType): Promise<any> {
        const clipboardItems = await this.clipboardDeviceDelegate.read<ClipboardItems>();
        if (!clipboardItems) return {};
        const [clipboardItem] = clipboardItems;
        const clipboardDataMap = {};
        // eslint-disable-next-line no-restricted-syntax
        for (const type of clipboardItem.types) {
            const blobData = await clipboardItem.getType(type);
            clipboardDataMap[type] = blobData && (await this.handlerBlobToString(type, blobData));
        }
        if (clipboardType) {
            return clipboardDataMap[clipboardType];
        }
        // @ts-ignore
        return clipboardDataMap;
    }

    /**
     * 更新剪贴板内容
     * @param {ClipboardData} clipboardConfig
     */
    private updateClipboardData(clipboardConfig: ClipboardData) {
        Object.keys(clipboardConfig).forEach((type: ClipboardType) => {
            if (this.validClipboardTypeArr.includes(type)) {
                this.clipboardData[type] = clipboardConfig[type];
            }
        });
    }

    /**
     * 删除剪贴板内容
     * @param {ClipboardType[]} deleteItems 删除剪贴板选项
     */
    private deleteClipboardData(deleteItems?: ClipboardType[]) {
        if (deleteItems?.length) {
            deleteItems.forEach(deleteItem => delete this.clipboardData[deleteItem]);
        } else {
            this.clipboardData = {};
        }
    }

    /**
     * 写入剪贴板
     * @param clipboardEvent {ClipboardEvent|undefined} 剪贴板事件，使用同步api时需要
     * @param useAsyncApi 是否使用异步api
     */
    private async writeToClipboard(
        clipboardEvent?: ClipboardEvent | undefined,
        useAsyncApi?: boolean,
        syncClipboardItemConf?: Record<string, string>,
    ) {
        try {
            if (ua.isMobile) {
                await this.mobileWriteToClipboard(syncClipboardItemConf);
            } else {
                await this.pcWriteToClipboard(clipboardEvent, useAsyncApi, syncClipboardItemConf);
            }
            this.handler?.successWrite();
            window.log.info('[ClipboardManager] copy to success');
        } catch (error) {
            this.handler?.failWrite();
            window.log.error(`[ClipboardManager] copy to fail:${error?.toString()}`);
        }
        this.handler?.finallyWrite(this.clipboardData, useAsyncApi);
        this.deleteClipboardData();
    }


    private startWriteTimer() {
        this.startWriteTime = new Date();
        clearTimeout(this.writeTimer);
        this.writeLoading?.close();
        this.writeTimer = setTimeout(() => {
            this.writeLoading = snackbar.show({
                message: i18n.t('正在复制中'),
                autoClose: false,
                type: 'loading',
                id: 'copying',
            });
        }, 200);
    }

    private endWriteTimer() {
        const currentTime = new Date();
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.COPY_PASTE,
            action: TDWActionType.COMMON_COPY,
            ver6: Date.now() - this.startCopyTime,
            ver7: this.copyDataType,
        });
        const writingTime = Number(currentTime) - Number(this.startWriteTime || currentTime);
        if (writingTime < 1000 && writingTime > 200) {
            // 小于1s的loading拉到1s，防止闪烁
            const loadingTimer = setTimeout(() => {
                this.startWriteTime = null;
                this.writeLoading?.close();
                clearTimeout(this.writeTimer);
                clearTimeout(loadingTimer);
            }, 1000 - writingTime);
        } else {
            this.startWriteTime = null;
            this.writeLoading?.close();
            clearTimeout(this.writeTimer);
        }
    }

    private async pcWriteToClipboard(
        clipboardEvent: ClipboardEvent | undefined,
        useAsyncApi: boolean | undefined,
        syncClipboardItemConf?: Record<string, string>,
    ) {
        if (!useAsyncApi) {
            return this.syncWriteToClipboard(clipboardEvent);
        }
        // 产品不要弹窗了。webkit 浏览器表现随缘。
        await this.CEFWriteToClipboard(syncClipboardItemConf);
        // if (this.isCEFBrowser) {
        //     await this.CEFWriteToClipboard();
        // } else {
        //     await this.webkitWriteToClipboard();
        // }
    }

    /**
     * 移动端写入客户端中剪贴板数据
     * @returns
     */
    private async mobileWriteToClipboard(syncClipboardItemConf?: Record<string, string>) {
        const clipboardDataMap = syncClipboardItemConf || this.getSyncClipboardItemConf();
        const dataMap: any = {};
        const typeTransMap = {
            [ClipboardType.HTML]: 'html',
            [ClipboardType.TEXT]: 'plain',
        };
        [ClipboardType.HTML, ClipboardType.TEXT].forEach((type) => {
            if (type in clipboardDataMap) {
                dataMap[typeTransMap[type]] = clipboardDataMap[type];
            }
        });

        window.log.info('[ClipboardManager] write to clipboard data:', dataMap);

        return await this.clipboardDeviceDelegate.write({
            params: {
                memory: {
                    dataMap,
                },
                adapter: {
                    ...dataMap,
                },
            },
            options: {
                useMemoryClipboardExtra: true, // 走内存同时，尝试走系统，这样复制到外面也能生效
            },
        });
    }

    private isSupportImageType(clipboardType: string) {
        return SUPPORT_IMAGE_TYPES.includes(clipboardType);
    }

    /**
     * 剪贴板blob数据转string的过程
     * @param {string} clipboardType
     * @param {Blob} blobData
     * @returns
     */
    private handlerBlobToString(clipboardType: string, blobData: Blob) {
        // 目前暂时不对图片blob处理
        if (this.isSupportImageType(clipboardType)) {
            return blobData;
        }
        const blobToStringMethod = {
            [ClipboardType.TEXT]: textData => this.textPreparePlugin.textBlobToText(textData),
            [ClipboardType.HTML]: htmlData => this.htmlPreparePlugin.HTMLblobToText(htmlData),
        };
        return blobToStringMethod[clipboardType]?.(blobData);
    }

    private handlerStringToString(clipboardType: string, stringData: string | undefined) {
        const stringToStringMethod = {
            [ClipboardType.TEXT]: textData => textData,
            [ClipboardType.HTML]: htmlData => this.htmlPreparePlugin.HTMLStringToText(htmlData),
        };
        return stringToStringMethod[clipboardType]?.(stringData);
    }

    /**
     * 获取 blob 型剪贴板配置，搭配异步api使用
     * @param {ClipboardType} clipboardType
     * @returns {Function} 对用类型的方法
     */
    private getClipboardTypeToBlobMethod(clipboardType: ClipboardType) {
        const clipboardTypeToBlobMethod = {
            [ClipboardType.IMAGE_PNG]: ({ container, outputOptions }) => this.imagePreparePlugin
                .getImageBlobAndString(container, outputOptions, this.contentRendererService),
            [ClipboardType.TEXT]: textData => this.textPreparePlugin.getTextBlob(textData),
            [ClipboardType.HTML]: (
                htmlData,
                imageBlobAndString,
            ) => this.htmlPreparePlugin.getHTMLBlob(htmlData, imageBlobAndString, this.handler?.getBase64UrlMap()),
        };
        return clipboardTypeToBlobMethod[clipboardType];
    }

    /**
     * 获取 string 型剪贴板配置，搭配同步api使用
     * @param {ClipboardType} clipboardType
     * @returns {Function} 对用类型的方法
     */
    private getClipboardTypeToStringMethod(clipboardType: ClipboardType) {
        const clipboardTypeToBlobMethod = {
            [ClipboardType.TEXT]: textData => this.textPreparePlugin.getTextString(textData),
            [ClipboardType.HTML]: (
                htmlData,
                imageBlobAndString,
            ) => this.htmlPreparePlugin.getHTMLString(
                htmlData,
                imageBlobAndString,
                true,
                this.handler?.getBase64UrlMap(),
            ),
        };
        return clipboardTypeToBlobMethod[clipboardType];
    }

    /**
     * 获取 webkit clipboardItem 方法
     * @returns clipboardItemConf
     */
    private getAsyncWebkitClipboardItemConf() {
        const clipboardItemConf = {};
        const copyClipboardData = { ...this.clipboardData };
        const imagePromise = this.preHandlerImageBlobAndString(copyClipboardData)
            .then((imageBlobAndString: ImageBlobAndString) => imageBlobAndString.imgBlob);
        clipboardItemConf[ClipboardType.IMAGE_PNG] = imagePromise;
        window.log.info('[ClipboardManager] getAsyncWebkitClipboardItemConf', clipboardItemConf);
        return clipboardItemConf;
    }

    /**
     * 获取chrome等默认浏览器 clipboardItem 方法
     * @returns clipboardItemConf
     */
    private async getAsyncCEFClipboardItemConf() {
        const clipboardItemConf = {};
        const copyClipboardData = { ...this.clipboardData };
        const imageBlobAndString = await this.preHandlerImageBlobAndString(copyClipboardData);
        clipboardItemConf[ClipboardType.IMAGE_PNG] = imageBlobAndString.imgBlob;
        // eslint-disable-next-line no-restricted-syntax
        for (const type of this.validClipboardTypeArr) {
            const clipboardItemValue = copyClipboardData[type];
            if (clipboardItemValue) {
                clipboardItemConf[type] = await this.getClipboardTypeToBlobMethod(type)(
                    clipboardItemValue,
                    imageBlobAndString,
                );
            }
        }
        window.log.info('[ClipboardManager] getAsyncCEFClipboardItemConf', clipboardItemConf);
        return clipboardItemConf;
    }

    /**
     * 这里预处理 Image 的原因是因为微软 PPT 不支持 image/png 格式
     * 所需把图片数据额外写入一份到 text/html 中
     * @param copyClipboardData
     * @returns
     */
    private preHandlerImageBlobAndString(copyClipboardData: ClipboardData): Promise<ImageBlobAndString> {
        const clipboardType = ClipboardType.IMAGE_PNG;
        if (!copyClipboardData[clipboardType]) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return Promise.resolve({} as ImageBlobAndString);
        }
        const clipboardItem = copyClipboardData[clipboardType];
        delete copyClipboardData[clipboardType];
        return this.getClipboardTypeToBlobMethod(clipboardType)(clipboardItem);
    }

    /**
     * 获取浏览器同步写剪贴板所需 clipboardItem
     * mobile 和 pc 通用
     * @returns clipboardItemConf
     */
    private getSyncClipboardItemConf(imageBlobAndString?: ImageBlobAndString) {
        const clipboardItemConf = {};
        this.validClipboardTypeArr.forEach((type: ClipboardType) => {
            const clipboardItemValue = this.clipboardData[type];
            if (this.clipboardData[type]) {
                clipboardItemConf[type] = this.getClipboardTypeToStringMethod(type)(
                    clipboardItemValue,
                    imageBlobAndString,
                );
            }
        });
        window.log.info('[ClipboardManager] getSyncClipboardItemConf', clipboardItemConf);
        return clipboardItemConf;
    }

    /**
     * chrome 等常规浏览器写剪贴板的方式
     * 同步api弊端：无法写入image/png格式
     * 异步api无明显弊端，无需额外用户权限兼容，clipboardItem 是 blob data，非 promise。
     * 现有策略：默认使用异步api。
     * @param clipboardEvent {ClipboardEvent|undefined} 剪贴板事件，同步api需要
     * @param useAsyncApi {boolean} 是否使用异步api，默认是
     * @returns
     */
    private async CEFWriteToClipboard(syncClipboardItemConf?: Record<string, string>) {
        window.log.info('[ClipboardManager] use CEF async api to write clipboard');
        const asyncClipboardItemConf = await this.getAsyncCEFClipboardItemConf();
        return this.clipboardDeviceDelegate.write({
            params: {
                adapter: {
                    syncClipboardItemConf,
                    asyncClipboardItemConf,
                },
            },
        });
    }

    /**
     * webkit 写剪贴板的方式，以下描述仅限于 Safari webkit
     * https://webkit.org/blog/10247/new-webkit-features-in-safari-13-1/
     * 同步api弊端：无法写入image/png格式
     * 异步api弊端安全性极高：
     * 1.必须通过用户click事件才能获得写入权限
     * 2.获得权限后至写剪贴板前不允许有异步操作
     *
     * 一共有2套写入策略：
     * 1.获取权限前准备好所有数据，与CEF一样，是 blob data。然后获取权限成功写入。
     * 2.获取权限前准备好Promise<blob>，然后获取权限写入成功。
     *
     * 策略1：不占用主线程，所有pending promise都在剪贴板的底层API里处理，缺点在于没法串行。比如当前业务中需要先 png 在 html。
     * 策略2：占用主线程，与CEF逻辑保持一致，简单可维护。
     * 现有策略：2
     *
     * @param useAsyncApi {boolean} 是否使用异步api，默认否
     * @param clipboardEvent {ClipboardEvent|undefined} 剪贴板事件，同步api需要
     * @returns
     */
    private async webkitWriteToClipboard() {
        window.log.info('[ClipboardManager] use webkit async api to write clipboard, need permission');
        const config = await this.getAsyncCEFClipboardItemConf();
        // const config = this.getAsyncWebkitClipboardItemConf();
        await this.handler?.getUserPermissionToCopyData?.();
        return this.clipboardDeviceDelegate.write({
            params: {
                adapter: {
                    asyncClipboardItemConf: config as any,
                },
            },
        });
    }

    /**
     * 同步写入剪贴板操作
     * webkit/chrome 没有差异化
     * 类型仅支持 text/plain、text/html
     * @param clipboardEvent {ClipboardEvent} 剪贴板事件
     * @returns {any} 是否写入成功
     */
    private syncWriteToClipboard(clipboardEvent?: ClipboardEvent): any {
        if (clipboardEvent) {
            const config = this.getSyncClipboardItemConf();
            return this.clipboardDeviceDelegate.write({
                params: {
                    adapter: {
                        event: clipboardEvent,
                        html: config[ClipboardType.HTML],
                        plain: config[ClipboardType.TEXT],
                    },
                    legacy: {
                        arr: [
                            {
                                format: ClipboardType.HTML,
                                data: config[ClipboardType.HTML],
                            },
                            {
                                format: ClipboardType.TEXT,
                                data: config[ClipboardType.TEXT],
                            },
                        ],
                    },
                },
            });
        }

        throw new Error('sync write to clipboard need clipboardEvent, the clipboardEvent is undefined');
    }
}

export default ClipboardManager;
